/* Date-only events use inclusive start/end dates in America/Phoenix. */
(() => {
  'use strict';
  if (window.NestEvents) { window.NestEvents.init(document); return; }
  const instances = new Map();
  const formatter = new Intl.DateTimeFormat('en-US', {timeZone:'America/Phoenix',year:'numeric',month:'2-digit',day:'2-digit'});
  function today(now = new Date()) {
    const parts = Object.fromEntries(formatter.formatToParts(now).map(part => [part.type,part.value]));
    return `${parts.year}-${parts.month}-${parts.day}`;
  }
  function validDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
    const parsed = new Date(`${value}T12:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0,10) === value;
  }
  function classify(start, end, day) {
    end = end || start;
    if (!validDate(start) || !validDate(end) || end < start) return 'undated';
    if (day < start) return 'upcoming';
    if (day > end) return 'past';
    return 'current';
  }
  function setStatus(element, status) {
    const label = element.querySelector('[data-event-status]');
    if (label) {
      label.textContent = {current:'Happening now',upcoming:'Upcoming',past:'Event ended',undated:'Dates to be announced'}[status];
      label.hidden = false;
      label.classList.toggle('nest-events__status--current',status === 'current');
      label.classList.toggle('nest-events__status--past',status === 'past');
    }
    element.querySelectorAll('[data-active-offer]').forEach(offer => { offer.hidden = !['current','upcoming'].includes(status) || !offer.textContent.trim(); });
    element.querySelectorAll('[data-ended-note]').forEach(note => { note.hidden = status !== 'past'; });
    const linkLabel = element.querySelector('[data-link-label]');
    if (linkLabel) linkLabel.textContent = status === 'past' ? 'View past event' : 'View event';
  }
  class Overview {
    constructor(root) {
      this.root = root;
      this.source = root.querySelector('[data-event-source]');
      this.cards = new Map();
      this.controller = new AbortController();
      this.next = this.source?.dataset.nextUrl || '';
      this.visited = new Set();
      this.loading = false;
      this.lastDay = '';
      this.adopt(root);
      const currentPage = Number(this.source?.dataset.currentPage || 1);
      if (currentPage > 1) {
        const first = new URL(window.location.href);
        first.searchParams.set(this.source.dataset.pageParam || 'page','1');
        first.searchParams.delete('section_id');
        this.next = first.pathname + first.search;
      }
      this.retry = root.querySelector('[data-events-retry]');
      this.retry?.addEventListener('click',() => this.load(),{signal:this.controller.signal});
      this.refresh();
      if (this.next) this.load();
    }
    adopt(container) {
      container.querySelectorAll('[data-nest-event]').forEach(card => {
        const id = card.dataset.eventId;
        if (id && !this.cards.has(id)) this.cards.set(id,card);
      });
    }
    refresh() {
      const day = today();
      const groups = {current:[],upcoming:[],past:[],undated:[]};
      this.cards.forEach(card => {
        const status = classify(card.dataset.start,card.dataset.end,day);
        setStatus(card,status);
        card.classList.toggle('nest-events__event--current',status === 'current');
        card.classList.toggle('nest-events__event--past',status === 'past');
        groups[status].push(card);
      });
      for (const [status,cards] of Object.entries(groups)) {
        cards.sort((a,b) => {
          const first = status === 'past' ? (a.dataset.end || a.dataset.start) : a.dataset.start;
          const second = status === 'past' ? (b.dataset.end || b.dataset.start) : b.dataset.start;
          const order = (first || '').localeCompare(second || '');
          return (status === 'past' ? -order : order) || a.dataset.eventId.localeCompare(b.dataset.eventId);
        });
        const list = this.root.querySelector(`[data-event-list="${status}"]`);
        const group = this.root.querySelector(`[data-event-group="${status}"]`);
        if (list && group) { list.replaceChildren(...cards); group.hidden = cards.length === 0; }
      }
      this.root.querySelector('[data-grouped-events]').hidden = false;
      if (this.source) this.source.hidden = true;
      const empty = this.root.querySelector('[data-events-empty]');
      if (empty) empty.hidden = this.cards.size !== 0 || !!this.next;
      this.lastDay = day;
    }
    async getPage(url) {
      const abort = new AbortController();
      const onAbort = () => abort.abort();
      this.controller.signal.addEventListener('abort',onAbort,{once:true});
      const timeout = window.setTimeout(() => abort.abort(),15000);
      try {
        const response = await fetch(url.href,{credentials:'same-origin',headers:{Accept:'text/html'},signal:abort.signal});
        if (!response.ok) throw new Error('Event page unavailable');
        return await response.text();
      } finally { window.clearTimeout(timeout); this.controller.signal.removeEventListener('abort',onAbort); }
    }
    async load() {
      if (this.loading || !this.next || this.controller.signal.aborted) return;
      this.loading = true;
      if (this.retry) this.retry.hidden = true;
      const status = this.root.querySelector('[data-events-load-status]');
      const pagination = this.root.querySelector('[data-events-pagination]');
      if (status) { status.hidden=false; status.textContent='Loading the rest of our events…'; }
      try {
        let batches = 0;
        while (this.next) {
          if (this.controller.signal.aborted) return;
          if (++batches > 100) throw new Error('Archive exceeds the supported pagination range');
          const url = new URL(this.next,window.location.href);
          if (url.origin !== window.location.origin) throw new Error('Unexpected event URL');
          url.searchParams.set('section_id',this.root.dataset.sectionId);
          const current = new URL(window.location.href);
          for (const key of ['preview_theme_id','view']) { if (current.searchParams.has(key)) url.searchParams.set(key,current.searchParams.get(key)); }
          if (this.visited.has(url.href)) throw new Error('Repeated event pagination');
          const html = await this.getPage(url);
          const documentPage = new DOMParser().parseFromString(html,'text/html');
          const pageRoot = Array.from(documentPage.querySelectorAll('[data-nest-overview]')).find(element => element.dataset.sectionId === this.root.dataset.sectionId);
          const source = pageRoot?.querySelector('[data-event-source]');
          if (!source) throw new Error('Missing event section');
          this.adopt(source);
          this.visited.add(url.href);
          this.next = source.dataset.nextUrl || '';
          this.refresh();
        }
        if (pagination) pagination.hidden=true;
        if (status) {status.textContent='';status.hidden=true;}
      } catch (error) {
        if (!this.controller.signal.aborted) {
          if (status) {status.hidden=false;status.textContent='Some events could not be loaded. Retry, or browse the next page.';}
          if (pagination) {
            pagination.hidden=false;
            const link = pagination.querySelector('[data-next-page]');
            if (link && this.next) {link.href=this.next;link.hidden=false;}
          }
          if (this.retry) this.retry.hidden=false;
        }
      } finally {this.loading=false;}
    }
    dispose() {this.controller.abort();}
  }
  function refreshDetail(root) {setStatus(root,classify(root.dataset.start,root.dataset.end,today()));}
  function init(scope = document) {
    const roots = scope.matches?.('[data-nest-overview],[data-nest-detail]') ? [scope] : [];
    roots.push(...scope.querySelectorAll('[data-nest-overview],[data-nest-detail]'));
    roots.forEach(root => {
      if (root.hasAttribute('data-nest-detail')) {refreshDetail(root);return;}
      if (!instances.has(root)) instances.set(root,new Overview(root));
    });
  }
  window.NestEvents = {init,helpers:{today,validDate,classify}};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => init(),{once:true}); else init();
  document.addEventListener('shopify:section:load',event => init(event.target));
  document.addEventListener('shopify:section:unload',event => {
    instances.forEach((instance,root) => {if (event.target.contains(root)) {instance.dispose();instances.delete(root);}});
  });
  window.setInterval(() => {
    instances.forEach((instance,root) => {
      if (!root.isConnected) {instance.dispose();instances.delete(root);}
      else if (instance.lastDay !== today()) instance.refresh();
    });
    document.querySelectorAll('[data-nest-detail]').forEach(refreshDetail);
  },60000);
})();
